import TransactionI from '../interfaces/Transaction';
import { displayTransactionName, displayStageThenName } from '../functions/displayTransactionName';
import getHour from '../functions/getHour';
import isRealPaidEntryFeeTx, {
    isSsoPresenceStub,
} from '../functions/isRealPaidEntryFeeTx';
import EditIcon from '../img/edit.svg';
import DeleteIcon from '../img/delete.svg';
import TransactionTableI, { WeeklyPointState } from '../interfaces/TransactionTable';

/**
 * Build the tooltip body for the Points cell. Surfaces the diagnostic
 * the user asked for inline (admin-visible on hover):
 *  - transaction source tag
 *  - business dates counted
 *  - live computed points (current + projected)
 *  - stored snapshot (tx.points)
 *  - mismatch flag
 *
 * Kept verbose because this exists specifically so staff can confirm
 * why a row shows the number it shows.
 */
const buildPointsTooltip = (
    primary: TransactionI,
    state: WeeklyPointState | undefined,
): string => {
    const lines: string[] = [];
    lines.push(`guestID: ${(primary as any).guestID || '—'}`);
    lines.push(`source: ${(primary as any).source || '(none)'}`);
    if (state) {
        lines.push(
            `period: ${state.weekStart.slice(0, 10)} → ${state.weekEnd.slice(0, 10)}`,
        );
        lines.push(`today: ${state.todayBusinessDate}`);
        lines.push(
            `attendanceDates: ${state.attendanceDates.join(', ') || '(none)'}`,
        );
        lines.push(`sources: ${state.sources.join(', ') || '(none)'}`);
        lines.push(`computed current: ${state.current}`);
        lines.push(`computed projected: ${state.projected}`);
        lines.push(`stored snapshot: ${primary.points ?? 0}`);
        const mismatch =
            Number(primary.points ?? 0) !== state.projected;
        lines.push(`mismatch: ${mismatch ? 'YES' : 'no'}`);
    } else {
        lines.push(`(live state not loaded — showing stored snapshot)`);
        lines.push(`stored snapshot: ${primary.points ?? 0}`);
    }
    return lines.join('\n');
};

const PURCHASE_FEES = new Set(['VIP Band', 'Jukebox']);

interface AggregatedRow {
    primary: TransactionI;
    vipBandPaid: number;
    jukeboxPaid: number;
    extraPaid: number;
}

interface AggregatedRowExt extends AggregatedRow {
    isPending: boolean;
    paidEntryFeeValue: number;
}

const aggregateByStage = (txs: TransactionI[]): AggregatedRowExt[] => {
    const groups: Record<string, TransactionI[]> = {};
    txs.forEach((t) => {
        const key = (t as any).guestID || t.stageName || t._id;
        if (!groups[key]) groups[key] = [];
        groups[key].push(t);
    });
    const out: AggregatedRowExt[] = Object.keys(groups).map((key) => {
        const group = groups[key];
        // Pick primary in priority: real paid AAEventFee > SSO stub
        // (so the row shows up even when unpaid) > first non-purchase
        // (legacy fallback).
        const paid = group.find(isRealPaidEntryFeeTx);
        const stub = group.find(isSsoPresenceStub);
        const fallback =
            group.find((t) => !PURCHASE_FEES.has(t.fee)) || group[0];
        const primary = paid || stub || fallback;
        const isPending = !paid && !!stub;

        let vipBandPaid = 0;
        let jukeboxPaid = 0;
        let extraPaid = 0;
        let paidEntryFeeValue = 0;
        group.forEach((t) => {
            const v = Number((t as any).value) || 0;
            if ((t as any).voided === true) return; // voided never counts
            if (t.fee === 'VIP Band') {
                vipBandPaid += v;
            } else if (t.fee === 'Jukebox') {
                jukeboxPaid += v;
            }
            // Only real paid entry fees and non-AAEventFee charges
            // contribute to the per-row Paid column.
            const isPaidEntry = isRealPaidEntryFeeTx(t);
            if (isPaidEntry) paidEntryFeeValue += v;
            if (t._id !== primary._id && (isPaidEntry || t.fee !== 'AAEventFee')) {
                extraPaid += v;
            }
        });
        return {
            primary,
            vipBandPaid,
            jukeboxPaid,
            extraPaid,
            isPending,
            paidEntryFeeValue,
        };
    });
    return out.sort(
        (a, b) =>
            new Date(a.primary.createdAt as any).getTime() -
            new Date(b.primary.createdAt as any).getTime()
    );
};

const TransactionTable = ({
    transactions,
    handleEdit,
    handleEditTransaction,
    openModal,
    allAccess = false,
    livePoints,
}: TransactionTableI) => {
    if (!transactions) {
        return <></>;
    }

    if (!allAccess) {
        return (
            <table className="transaction-container__form--table">
                <thead>
                    <tr>
                        <th>Name</th>
                        <th>Check-In Time</th>
                        <th>Paid</th>
                        <th>Edit</th>
                        <th>Delete</th>
                    </tr>
                </thead>
                <tbody>
                    {[...transactions]
                        .filter((t: any) => t.voided !== true)
                        .sort(
                            (a: TransactionI, b: TransactionI) =>
                                new Date(a.createdAt as any).getTime() -
                                new Date(b.createdAt as any).getTime()
                        )
                        .map((tx: TransactionI) => {
                            const { _id, createdAt, value } = tx;
                            return (
                            <tr id={_id} key={_id}>
                                <td
                                    onClick={handleEdit}
                                    className="transaction-container__form--table-stage"
                                >
                                    {/*
                                      Pass the FULL tx (not just
                                      firstName/lastName) so the
                                      helper can fall through to
                                      stageName + guestID tiers when
                                      the snapshot was anonymized.
                                      Previously this branch dropped
                                      stageName + guestID, which
                                      forced the helper to bottom out
                                      at "Unknown Guest" for any
                                      tx whose firstName/lastName
                                      were "[purged]".
                                    */}
                                    {displayTransactionName(tx as any).toUpperCase()}
                                </td>
                                <td className="transaction-container__form--table-time">
                                    {getHour(createdAt)}
                                </td>
                                <td className="transaction-container__form--table-value">
                                    {value}$
                                </td>
                                <td className="transaction-container__form--table-value">
                                    <button
                                        className="user-button-icon"
                                        onClick={handleEditTransaction}
                                    >
                                        <img
                                            className={_id}
                                            src={EditIcon}
                                            alt="edit Icon"
                                        />
                                    </button>
                                </td>
                                <td className="transaction-container__form--table-value">
                                    <button
                                        className="user-button-icon"
                                        onClick={openModal}
                                    >
                                        <img
                                            className={_id}
                                            src={DeleteIcon}
                                            alt="delete Icon"
                                        />
                                    </button>
                                </td>
                            </tr>
                            );
                        })}
                </tbody>
            </table>
        );
    }

    const rows = aggregateByStage(transactions);

    return (
        <table className="transaction-container__form--table">
            <thead>
                <tr>
                    <th>Stage</th>
                    <th>Check-In Time</th>
                    <th>Paid</th>
                    <th>Points</th>
                    <th>VIP Band</th>
                    <th>Jukebox</th>
                    <th>Edit</th>
                    <th>Delete</th>
                </tr>
            </thead>
            <tbody>
                {rows.map(
                    ({
                        primary,
                        vipBandPaid,
                        jukeboxPaid,
                        extraPaid,
                        isPending,
                        paidEntryFeeValue,
                    }) => {
                        const totalPaid =
                                paidEntryFeeValue + extraPaid;
                        return (
                                <tr id={primary._id} key={primary._id}>
                                    <td
                                        onClick={handleEdit}
                                        className="transaction-container__form--table-stage"
                                    >
                                        {displayStageThenName(primary).toUpperCase()}
                                    </td>
                                    <td className="transaction-container__form--table-time">
                                        {getHour(primary.createdAt)}
                                    </td>
                                    <td className="transaction-container__form--table-value">
                                        {isPending ? (
                                            <span
                                                style={{
                                                    background:
                                                        'rgba(241,196,15,0.18)',
                                                    color: '#f1c40f',
                                                    border: '1px solid #f1c40f',
                                                    padding: '2px 8px',
                                                    borderRadius: 999,
                                                    fontSize: '0.75rem',
                                                    fontWeight: 700,
                                                    whiteSpace: 'nowrap',
                                                }}
                                            >
                                                Pending Entry Fee
                                            </span>
                                        ) : (
                                            `${totalPaid}\$`
                                        )}
                                    </td>
                                <td className="transaction-container__form--table-value">
                                    {(() => {
                                        const gid = (primary as any).guestID;
                                        const state = gid
                                            ? livePoints?.[gid]
                                            : undefined;
                                        // Live wins for every AAEventFee row
                                        // (paid or stub). Falls back to the
                                        // stored snapshot when the live map
                                        // isn't loaded yet.
                                        const displayPoints =
                                            primary.fee === 'AAEventFee' &&
                                            state
                                                ? state.projected
                                                : primary.points;
                                        const mismatch =
                                            !!state &&
                                            Number(primary.points ?? 0) !==
                                                state.projected;
                                        return (
                                            <span
                                                title={buildPointsTooltip(
                                                    primary,
                                                    state,
                                                )}
                                                style={{
                                                    cursor: 'help',
                                                    color: mismatch
                                                        ? '#e67e22'
                                                        : undefined,
                                                    fontWeight: mismatch
                                                        ? 700
                                                        : undefined,
                                                }}
                                            >
                                                {displayPoints}
                                                {mismatch && (
                                                    <span
                                                        style={{
                                                            marginLeft: 4,
                                                            fontSize: '0.7rem',
                                                            opacity: 0.8,
                                                        }}
                                                    >
                                                        ⚠
                                                    </span>
                                                )}
                                            </span>
                                        );
                                    })()}
                                </td>
                                <td className="transaction-container__form--table-value">
                                    {vipBandPaid > 0 ? `${vipBandPaid}$` : '—'}
                                </td>
                                <td className="transaction-container__form--table-value">
                                    {jukeboxPaid > 0 ? `${jukeboxPaid}$` : '—'}
                                </td>
                                <td className="transaction-container__form--table-value">
                                    <button
                                        className="user-button-icon"
                                        onClick={handleEditTransaction}
                                    >
                                        <img
                                            className={primary._id}
                                            src={EditIcon}
                                            alt="edit Icon"
                                        />
                                    </button>
                                </td>
                                <td className="transaction-container__form--table-value">
                                    <button
                                        className="user-button-icon"
                                        onClick={openModal}
                                    >
                                        <img
                                            className={primary._id}
                                            src={DeleteIcon}
                                            alt="delete Icon"
                                        />
                                    </button>
                                    </td>
                                </tr>
                        );
                    }
                )}
            </tbody>
        </table>
    );
};

export default TransactionTable;
